// UserFiles.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { authFetch } from '../../utils/authFetch';

import {
  DataTable,
  Button,
  Modal,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Tooltip,
  InlineLoading,
  Loading,
} from '@carbon/react';

import {
  Demo,
  DocumentDownload,
  DocumentView,
  Upload,
  TrashCan,
  Renew,
  Information,
  Edit,
  Play,
} from '@carbon/react/icons';

import ReactMarkdown from 'react-markdown';

import { v4 as uuidv4 } from 'uuid';

export const MEMORY_FILE = 'user_memory.md';
export const MEMORY_CHAR_LIMIT = 20000;

export const isUserMemory = fileName => fileName === MEMORY_FILE;

/**
 * Parse a date value from the backend into a Date object.
 * Handles ISO 8601 ("2025-01-01T00:00:00Z"), Python strftime
 * "%Y-%m-%d %H:%M:%S %Z%z" ("2026-09-12 14:30:00 UTC+0000"),
 * and numeric timestamps (seconds or milliseconds).
 * Returns null for absent or unparseable values.
 */
export function parseDate(value) {
  if (value == null || value === '') return null;

  if (typeof value === 'number') {
    const ms = value < 1e12 ? value * 1000 : value;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }

  const str = String(value);

  // Try native parse first (handles ISO 8601)
  let d = new Date(str);
  if (!isNaN(d.getTime())) return d;

  // Handle Python strftime "%Y-%m-%d %H:%M:%S %Z%z"
  // e.g. "2026-09-12 14:30:00 UTC+0000" → "2026-09-12T14:30:00+00:00"
  const m = str.match(
    /^(\d{4}-\d{2}-\d{2})\s(\d{2}:\d{2}:\d{2})\s[A-Za-z]{2,5}([+-])(\d{2})(\d{2})$/
  );
  if (m) {
    d = new Date(`${m[1]}T${m[2]}${m[3]}${m[4]}:${m[5]}`);
    if (!isNaN(d.getTime())) return d;
  }

  return null;
}

/**
 * Format a date value for display. Returns a locale date string,
 * or an em dash ("\u2014") for genuinely absent/unparseable values.
 */
export function formatDate(value) {
  const d = parseDate(value);
  return d ? d.toLocaleDateString() : '\u2014';
}

const CREATED_SENTINELS = ['Unknown'];
const EXPIRES_SENTINELS = ['No expiration'];

const UserFiles = ({ token, isActive, user }) => {
  const [userFileList, setUserFileList] = useState([]);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteFileName, setDeleteFileName] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: 'created_at',
    direction: 'DESC',
  });

  // Restore selectedFile from localStorage for tab persistence
  const [selectedFile, setSelectedFile] = useState(() => {
    return localStorage.getItem('userFiles_selectedFile') || null;
  });

  // Restore isModalOpen from localStorage
  const [isModalOpen, setModalOpen] = useState(() => {
    const saved = localStorage.getItem('userFiles_isModalOpen');
    return saved ? JSON.parse(saved) : false;
  });

  // FIXED: Never initialize fileContent from localStorage. Always null, always fetch fresh.
  const [fileContent, setFileContent] = useState(null);

  const [isLoading, setLoading] = useState(false);
  const [loadingFileList, setLoadingFileList] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const fileInputRef = useRef(null);

  // Stale-fetch guard: each fetch increments this; responses check before applying
  const fetchIdRef = useRef(0);

  // Recovery draft: read once from legacy localStorage on mount.
  // Only relevant for user_memory.md. Preserved until user explicitly dismisses.
  const [recoveryDraft, setRecoveryDraft] = useState(() => {
    const cached = localStorage.getItem('userFiles_fileContent');
    const cachedFile = localStorage.getItem('userFiles_selectedFile');
    if (cached && cachedFile === MEMORY_FILE) {
      return cached;
    }
    return null;
  });
  const [showRecoveryPreview, setShowRecoveryPreview] = useState(false);

  // Automatic-updates status for user_memory.md
  const [memoryStatus, setMemoryStatus] = useState(null);
  const [isResumingAutomatic, setIsResumingAutomatic] = useState(false);
  const [showResumeConfirm, setShowResumeConfirm] = useState(false);

  const isEditableFile = fileName => {
    if (!fileName) return false;
    const editableExts = [
      '.md',
      '.txt',
      '.json',
      '.yaml',
      '.yml',
      '.log',
      '.csv',
      '.html',
      '.css',
      '.js',
      '.ts',
      '.py',
      '.sh',
    ];
    return editableExts.some(ext => fileName.toLowerCase().endsWith(ext));
  };

  const handleConfirmDelete = async () => {
    if (!deleteFileName) return;

    setIsDeleting(true);
    try {
      const url = `mlapi/premium/task/data/delete/files/${encodeURIComponent(deleteFileName)}`;
      const response = await authFetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to delete the file. Status: ${response.status}`
        );
      }

      // If the deleted file is the selectedFile, close the modal
      if (deleteFileName === selectedFile) {
        handleCloseModal();
      }

      // Refresh the file list after successful deletion
      await fetchUserFileList();
    } catch (error) {
      console.error('Error deleting the file:', error);
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setDeleteFileName(null);
    }
  };

  // Save selectedFile to localStorage whenever it changes
  useEffect(() => {
    if (selectedFile !== null) {
      localStorage.setItem('userFiles_selectedFile', selectedFile);
    } else {
      localStorage.removeItem('userFiles_selectedFile');
    }
  }, [selectedFile]);

  // Save isModalOpen to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('userFiles_isModalOpen', JSON.stringify(isModalOpen));
  }, [isModalOpen]);

  // REMOVED: No longer write fileContent to userFiles_fileContent localStorage.
  // Legacy cached value is preserved solely as recoveryDraft state.

  // Detect screen size to adjust for mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Set initial value

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Define handleCloseModal before fetchUserFileList
  const handleCloseModal = useCallback(() => {
    // Invalidate any in-flight fetches
    fetchIdRef.current += 1;
    setModalOpen(false);
    setSelectedFile(null);
    setFileContent(null);
    setIsEditing(false);
    setEditContent('');
    setSaveError(null);
    setShowRecoveryPreview(false);
    setMemoryStatus(null);
    setShowResumeConfirm(false);
  }, []);

  // Wrap fetchUserFileList in useCallback and move it before the useEffect that uses it
  const fetchUserFileList = useCallback(async () => {
    const url = `mlapi/premium/task/data/list_detail/files`;
    try {
      setLoadingFileList(true); // Start loading file list
      const response = await authFetch(url, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      setUserFileList(data);

      // Check if selectedFile is still in the list
      if (selectedFile) {
        const fileExists = data.some(file => file.name === selectedFile);
        if (!fileExists) {
          // File no longer exists, close modal
          handleCloseModal();
        }
      }
    } catch (error) {
      console.error('Error fetching user files:', error);
    } finally {
      setLoadingFileList(false); // Stop loading file list
    }
  }, [token, selectedFile, handleCloseModal]);

  const getSortedFiles = files => {
    if (!sortConfig.key) return files;

    return [...files].sort((a, b) => {
      let valueA = a[sortConfig.key];
      let valueB = b[sortConfig.key];

      // Handle date comparisons
      if (sortConfig.key === 'created_at' || sortConfig.key === 'expires_at') {
        valueA = parseDate(valueA) || 0;
        valueB = parseDate(valueB) || 0;
      }

      // Handle numeric comparison for size
      if (sortConfig.key === 'size') {
        valueA = Number(valueA);
        valueB = Number(valueB);
      }

      // Handle string comparison for name
      if (sortConfig.key === 'name') {
        valueA = valueA.toLowerCase();
        valueB = valueB.toLowerCase();
      }

      if (valueA > valueB) return sortConfig.direction === 'ASC' ? 1 : -1;
      if (valueA < valueB) return sortConfig.direction === 'ASC' ? -1 : 1;
      return 0;
    });
  };

  // Fetch file list on component mount or when token or isActive changes
  useEffect(() => {
    if (token && isActive) {
      fetchUserFileList();
    } else {
      setUserFileList([]);
    }
  }, [token, isActive, fetchUserFileList]);

  // Fetch file content — uses memory endpoint for user_memory.md, binary for others.
  // Protected against stale responses via fetchIdRef.
  const fetchFileContent = useCallback(
    async fileName => {
      const myFetchId = ++fetchIdRef.current;
      setLoading(true);
      setSaveError(null);

      const url = isUserMemory(fileName)
        ? 'mlapi/basic/user/memory'
        : `mlapi/premium/task/data/get_binary/files/${encodeURIComponent(fileName)}`;

      try {
        const response = await authFetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(isUserMemory(fileName) ? { Accept: 'text/plain' } : {}),
          },
        });
        if (!response.ok) {
          throw new Error(
            `Failed to fetch file content. Status: ${response.status}`
          );
        }

        // Guard against stale response (file was switched or modal closed)
        if (fetchIdRef.current !== myFetchId) return;

        const content = await response.text();

        // Guard again after body parse
        if (fetchIdRef.current !== myFetchId) return;

        setFileContent(content);
      } catch (error) {
        if (fetchIdRef.current === myFetchId) {
          console.error('Error fetching file content:', error);
        }
      } finally {
        if (fetchIdRef.current === myFetchId) {
          setLoading(false);
        }
      }
    },
    [token]
  );

  // Fetch memory status (automatic updates paused/active)
  const fetchMemoryStatus = useCallback(async () => {
    try {
      const response = await authFetch('mlapi/basic/user/memory/status', {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) return;
      const data = await response.json();
      setMemoryStatus(data);
    } catch (error) {
      console.error('Error fetching memory status:', error);
    }
  }, [token]);

  // Resume automatic updates (POST /memory/automatic)
  const handleResumeAutomatic = async () => {
    setIsResumingAutomatic(true);
    try {
      const response = await authFetch('mlapi/basic/user/memory/automatic', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        await fetchMemoryStatus();
      }
    } catch (error) {
      console.error('Error resuming automatic updates:', error);
    } finally {
      setIsResumingAutomatic(false);
      setShowResumeConfirm(false);
    }
  };

  // FIXED: Always fetch fresh when modal opens — no `!fileContent` guard.
  useEffect(() => {
    if (isModalOpen && selectedFile) {
      fetchFileContent(selectedFile);
      // Also fetch memory status when viewing user_memory.md
      if (isUserMemory(selectedFile)) {
        fetchMemoryStatus();
      }
    }
  }, [isModalOpen, selectedFile, fetchFileContent, fetchMemoryStatus]);

  // Clear saved state when token becomes falsy (user logs out)
  // NOTE: intentionally NOT removing userFiles_fileContent — recovery draft preserved
  useEffect(() => {
    if (!token) {
      localStorage.removeItem('userFiles_selectedFile');
      localStorage.removeItem('userFiles_isModalOpen');
      setSelectedFile(null);
      setModalOpen(false);
      setFileContent(null);
      setUserFileList([]);
    }
  }, [token]);

  const handleDownload = async fileName => {
    const url = `mlapi/premium/task/data/get_binary/files/${encodeURIComponent(fileName)}`;
    try {
      const response = await authFetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      a.remove();
    } catch (error) {
      console.error('Error downloading the file', error);
    }
  };

  const handleFileUpload = async event => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    const url = `mlapi/premium/task/data/upload/files/${encodeURIComponent(file.name)}`;
    try {
      setLoading(true);
      const response = await authFetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      if (response.ok) {
        await fetchUserFileList(); // Refresh file list after upload
      } else {
        console.error('File upload failed.');
      }
    } catch (error) {
      console.error('Error uploading the file:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewFile = fileName => {
    // Invalidate any prior in-flight fetch
    fetchIdRef.current += 1;
    // Reset state before fetching fresh content
    setFileContent(null);
    setSelectedFile(fileName);
    setModalOpen(true);
    setIsEditing(false);
    setEditContent('');
    setSaveError(null);
    setShowRecoveryPreview(false);
    setMemoryStatus(null);
    setShowResumeConfirm(false);
    // Fetch file content will be triggered by useEffect
  };

  const handleEditFile = () => {
    // Only enter edit with fresh server content — never from localStorage
    if (fileContent === null) return;
    setEditContent(fileContent);
    setIsEditing(true);
    setSaveError(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditContent('');
    setSaveError(null);
  };

  const handleSaveFile = async () => {
    if (!selectedFile) return;

    // Enforce character limit for user_memory.md
    if (isUserMemory(selectedFile) && editContent.length > MEMORY_CHAR_LIMIT) {
      setSaveError(
        `Content exceeds ${MEMORY_CHAR_LIMIT.toLocaleString()} character limit.`
      );
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      let response;

      if (isUserMemory(selectedFile)) {
        // Dedicated PUT JSON endpoint for user_memory.md
        response = await authFetch('mlapi/basic/user/memory', {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ content: editContent }),
        });
      } else {
        // Multipart upload for all other files
        const blob = new Blob([editContent], { type: 'text/plain' });
        const formData = new FormData();
        formData.append('file', blob, selectedFile);
        const url = `mlapi/premium/task/data/upload/files/${encodeURIComponent(selectedFile)}`;
        response = await authFetch(url, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
      }

      if (!response.ok) {
        setSaveError(`Save failed (status ${response.status}). Please retry.`);
        return;
      }

      // Refetch and exact-compare before declaring success
      const verifyUrl = isUserMemory(selectedFile)
        ? 'mlapi/basic/user/memory'
        : `mlapi/premium/task/data/get_binary/files/${encodeURIComponent(selectedFile)}`;

      const verifyResponse = await authFetch(verifyUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isUserMemory(selectedFile) ? { Accept: 'text/plain' } : {}),
        },
      });

      if (!verifyResponse.ok) {
        setSaveError(
          'Save may have succeeded but verification failed. Please re-open the file.'
        );
        return;
      }

      const serverContent = await verifyResponse.text();

      if (serverContent !== editContent) {
        setSaveError(
          'Save verification failed: server content does not match. Your edits are preserved — please retry.'
        );
        // Keep editor open with editContent intact; update view content
        setFileContent(serverContent);
        return;
      }

      // Success — exit edit mode
      setFileContent(serverContent);
      setIsEditing(false);
      setEditContent('');
      setSaveError(null);

      // Refresh memory status — manual save sets manually_managed marker
      if (isUserMemory(selectedFile)) {
        fetchMemoryStatus();
      }
    } catch (error) {
      console.error('Error saving file:', error);
      setSaveError(`Save error: ${error.message}. Please retry.`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = fileName => {
    // Set state to show delete confirmation modal
    setDeleteFileName(fileName);
    setDeleteModalOpen(true);
  };

  // Recovery draft actions
  const handleDismissRecovery = () => {
    localStorage.removeItem('userFiles_fileContent');
    setRecoveryDraft(null);
    setShowRecoveryPreview(false);
  };

  const handleRestoreRecovery = () => {
    if (!recoveryDraft) return;
    setEditContent(recoveryDraft);
    setIsEditing(true);
    setShowRecoveryPreview(false);
    setSaveError(null);
  };

  const handleDownloadRecovery = () => {
    if (!recoveryDraft) return;
    const blob = new Blob([recoveryDraft], { type: 'text/markdown' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user_memory_cached_draft.md';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  };

  // Show recovery banner when viewing user_memory.md with a differing cached draft
  const showRecoveryBanner =
    recoveryDraft !== null &&
    selectedFile === MEMORY_FILE &&
    fileContent !== null &&
    recoveryDraft !== fileContent &&
    !isEditing;

  // Character limit helpers for editor
  const isOverLimit =
    isUserMemory(selectedFile) && editContent.length > MEMORY_CHAR_LIMIT;

  // Update filteredRows to use sorted data
  const filteredRows = userFileList.filter(file =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const sortedRows = getSortedFiles(filteredRows);

  const headers = [
    { key: 'name', header: 'File Name' },
    !isMobile && { key: 'created_at', header: 'Created At' },
    !isMobile && { key: 'expires_at', header: 'Expires At' },
    { key: 'size', header: 'Size' },
    { key: 'actions', header: 'Actions' },
  ].filter(Boolean);

  const rows = sortedRows.map(file => ({
    id: uuidv4(),
    name: file.name,
    created_at: CREATED_SENTINELS.includes(file.created_at)
      ? 'Unknown'
      : formatDate(file.created_at),
    size: (file.size / 1024).toFixed(2),
    expires_at: EXPIRES_SENTINELS.includes(file.expires_at)
      ? 'No Expiration'
      : formatDate(file.expires_at),
  }));

  const handleHeaderClick = headerKey => {
    const headerKeyToDataKey = {
      name: 'name',
      created_at: 'created_at',
      expires_at: 'expires_at',
      size: 'size',
    };

    const dataKey = headerKeyToDataKey[headerKey];
    if (!dataKey) return;

    setSortConfig(prev => ({
      key: dataKey,
      direction:
        prev.key === dataKey
          ? prev.direction === 'ASC'
            ? 'DESC'
            : 'ASC'
          : dataKey === 'created_at'
          ? 'DESC'
          : 'ASC',
    }));
  };

  const triggerFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRefresh = async () => {
    await fetchUserFileList();
  };

  return (
    <>
      Upload files for AI to analyze or tell it to save information in a file
      you can view or download from here.
      <Tooltip
        label="When using AI, check Use Search Tools to allow it to save and load data from your files. For example: Get today's news about XYZ and save it to a file with today's date-XYZ"
        align="bottom-right">
        <button className="sb-tooltip-trigger" type="button">
          <Information />
        </button>
      </Tooltip>
      {/* —— NEW: 2-minute video tutorial button —— */}
      &nbsp;&nbsp;
      <Tooltip
        label="Watch a 3-minute video tutorial about Files and Integrations."
        align="bottom"
        style={{ marginLeft: '8px' }}>
        <Button
          kind="ghost"
          size="sm"
          onClick={() =>
            window.open('https://youtu.be/Az7aISEsayk', '_blank', 'noopener')
          }
          className="tooltip-button"
          aria-label="Watch tutorial video">
          <Demo />
        </Button>
      </Tooltip>
      {loadingFileList ? (
        // Display loading indicator while loading file list
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            marginTop: '3em', // Adds approximately three blank lines of space
          }}>
          <Loading description="Loading files..." withOverlay={false} />
        </div>
      ) : (
        <TableContainer>
          <TableToolbar>
            <TableToolbarContent>
              <TableToolbarSearch
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                persistent
              />
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
              <Button
                renderIcon={Upload}
                iconDescription="Upload"
                onClick={triggerFileUpload}>
                Upload File
              </Button>
              <Button
                renderIcon={Renew}
                iconDescription="Refresh"
                onClick={handleRefresh}>
                Refresh List
              </Button>
            </TableToolbarContent>
          </TableToolbar>
          <DataTable
            rows={rows}
            headers={headers}
            render={({ rows, headers, getHeaderProps, getRowProps }) => (
              <Table>
                <TableHead>
                  <TableRow>
                    {headers.map(header => {
                      const isSorted =
                        header.key === 'name'
                          ? sortConfig.key === 'name'
                          : header.key === 'created_at'
                          ? sortConfig.key === 'created_at'
                          : header.key === 'expires_at'
                          ? sortConfig.key === 'expires_at'
                          : header.key === 'size'
                          ? sortConfig.key === 'size'
                          : false;

                      return (
                        <TableHeader
                          {...getHeaderProps({ header })}
                          key={header.key}
                          onClick={() => handleHeaderClick(header.key)}>
                          {header.header}
                          {isSorted && (
                            <span style={{ marginLeft: '8px' }}>
                              {sortConfig.direction === 'ASC' ? '↑' : '↓'}
                            </span>
                          )}
                        </TableHeader>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(row => (
                    <TableRow {...getRowProps({ row })} key={row.id}>
                      {row.cells.map(cell => (
                        <TableCell key={cell.id}>
                          {cell.info.header === 'actions' ? (
                            <>
                              <Button
                                renderIcon={DocumentDownload}
                                hasIconOnly
                                tooltipAlignment="center"
                                iconDescription="Download"
                                onClick={() =>
                                  handleDownload(row.cells[0].value)
                                }
                              />
                              <Button
                                renderIcon={DocumentView}
                                hasIconOnly
                                tooltipAlignment="center"
                                iconDescription="View"
                                onClick={() =>
                                  handleViewFile(row.cells[0].value)
                                }
                              />
                              <Button
                                renderIcon={TrashCan}
                                hasIconOnly
                                tooltipAlignment="center"
                                iconDescription="Delete"
                                onClick={() => handleDelete(row.cells[0].value)}
                              />
                            </>
                          ) : (
                            cell.value
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          />
        </TableContainer>
      )}
      <Modal
        open={isDeleteModalOpen}
        modalHeading="Delete file"
        danger
        primaryButtonText={isDeleting ? 'Deleting...' : 'Delete'}
        secondaryButtonText="Cancel"
        onRequestClose={() => {
          setDeleteModalOpen(false);
          setDeleteFileName(null);
        }}
        onRequestSubmit={handleConfirmDelete}
        primaryButtonDisabled={isDeleting}
        shouldSubmitOnEnter={!isDeleting}>
        <p style={{ marginBottom: '1rem' }}>
          Are you sure you want to permanently delete{' '}
          <strong>{deleteFileName}</strong>?
        </p>
        <p>This action cannot be undone.</p>
      </Modal>
      <Modal
        open={isModalOpen}
        modalHeading={
          isEditing ? `Editing ${selectedFile}` : `Viewing ${selectedFile}`
        }
        onRequestClose={isEditing ? handleCancelEdit : handleCloseModal}
        passiveModal
        size="lg"
        className="file-viewer-modal">
        {isLoading ? (
          // Display the loading indicator inside the modal while loading
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <InlineLoading description="Loading file content..." />
          </div>
        ) : (
          <>
            {/* Recovery banner for user_memory.md */}
            {showRecoveryBanner && (
              <div
                data-testid="recovery-banner"
                style={{
                  marginBottom: '0.75rem',
                  padding: '0.75rem',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '4px',
                }}>
                <p style={{ marginBottom: '0.5rem', fontWeight: 600 }}>
                  Cached draft found ({recoveryDraft.length.toLocaleString()}{' '}
                  chars)
                </p>
                <p style={{ marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                  A previously cached version of user_memory.md was found in
                  your browser. It differs from the current server version. You
                  can restore it into the editor (you must Save explicitly),
                  download it, or dismiss it.
                </p>
                <div
                  style={{
                    display: 'flex',
                    gap: '0.5rem',
                    flexWrap: 'wrap',
                  }}>
                  <Button
                    size="sm"
                    kind="tertiary"
                    onClick={() =>
                      setShowRecoveryPreview(!showRecoveryPreview)
                    }>
                    {showRecoveryPreview
                      ? 'Hide preview'
                      : 'Preview cached draft'}
                  </Button>
                  <Button size="sm" onClick={handleRestoreRecovery}>
                    Restore into editor
                  </Button>
                  <Button
                    size="sm"
                    kind="secondary"
                    onClick={handleDownloadRecovery}>
                    Download cached draft
                  </Button>
                  <Button
                    size="sm"
                    kind="danger--tertiary"
                    onClick={handleDismissRecovery}>
                    Dismiss cached draft
                  </Button>
                </div>
                {showRecoveryPreview && (
                  <pre
                    data-testid="recovery-preview"
                    style={{
                      marginTop: '0.75rem',
                      maxHeight: '200px',
                      overflow: 'auto',
                      backgroundColor: '#f4f4f4',
                      padding: '0.5rem',
                      fontSize: '0.75rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}>
                    {recoveryDraft}
                  </pre>
                )}
              </div>
            )}

            {/* Automatic updates status for user_memory.md */}
            {isUserMemory(selectedFile) && memoryStatus && !isEditing && (
              <div
                data-testid="memory-status"
                style={{
                  marginBottom: '0.75rem',
                  padding: '0.5rem 0.75rem',
                  backgroundColor: memoryStatus.manually_managed
                    ? '#fff8e1'
                    : '#e8f5e9',
                  border: `1px solid ${
                    memoryStatus.manually_managed ? '#ffcc02' : '#4caf50'
                  }`,
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  flexWrap: 'wrap',
                }}>
                <span>
                  Automatic updates:{' '}
                  {memoryStatus.manually_managed
                    ? 'Paused (manual edits protected)'
                    : 'On'}
                </span>
                {memoryStatus.manually_managed && !showResumeConfirm && (
                  <Button
                    size="sm"
                    kind="tertiary"
                    renderIcon={Play}
                    onClick={() => setShowResumeConfirm(true)}
                    disabled={isResumingAutomatic}>
                    Resume automatic updates
                  </Button>
                )}
                {showResumeConfirm && (
                  <div
                    data-testid="resume-confirm"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      flexWrap: 'wrap',
                    }}>
                    <span style={{ fontSize: '0.8125rem', color: '#525252' }}>
                      AI will rewrite this file automatically after
                      conversations. Your current content is preserved and
                      backed up.
                    </span>
                    <Button
                      size="sm"
                      onClick={handleResumeAutomatic}
                      disabled={isResumingAutomatic}>
                      {isResumingAutomatic ? 'Resuming...' : 'Confirm resume'}
                    </Button>
                    <Button
                      size="sm"
                      kind="secondary"
                      onClick={() => setShowResumeConfirm(false)}
                      disabled={isResumingAutomatic}>
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            )}

            {isEditableFile(selectedFile) && (
              <div
                style={{
                  marginBottom: '0.75rem',
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'center',
                }}>
                {isEditing ? (
                  <>
                    <Button
                      size="sm"
                      onClick={handleSaveFile}
                      disabled={isSaving || isOverLimit}>
                      {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      size="sm"
                      kind="secondary"
                      onClick={handleCancelEdit}
                      disabled={isSaving}>
                      Cancel
                    </Button>
                    {isUserMemory(selectedFile) && (
                      <span
                        data-testid="char-count"
                        style={{
                          fontSize: '0.75rem',
                          color: isOverLimit ? '#da1e28' : '#525252',
                          marginLeft: 'auto',
                        }}>
                        {editContent.length.toLocaleString()} /{' '}
                        {MEMORY_CHAR_LIMIT.toLocaleString()} chars
                        {isOverLimit && ' — over limit'}
                      </span>
                    )}
                  </>
                ) : (
                  <Button
                    size="sm"
                    kind="tertiary"
                    renderIcon={Edit}
                    onClick={handleEditFile}>
                    Edit
                  </Button>
                )}
              </div>
            )}

            {/* Save error banner */}
            {saveError && (
              <div
                data-testid="save-error"
                style={{
                  marginBottom: '0.75rem',
                  padding: '0.5rem 0.75rem',
                  backgroundColor: '#fff1f1',
                  border: '1px solid #da1e28',
                  borderRadius: '4px',
                  color: '#da1e28',
                  fontSize: '0.875rem',
                }}>
                {saveError}
              </div>
            )}

            {isEditing ? (
              <textarea
                data-testid="file-editor"
                value={editContent}
                onChange={e => setEditContent(e.target.value)}
                style={{
                  width: '100%',
                  minHeight: '60vh',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  lineHeight: '1.5',
                  padding: '0.75rem',
                  backgroundColor: '#f4f4f4',
                  border: `1px solid ${isOverLimit ? '#da1e28' : '#8d8d8d'}`,
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  outline: 'none',
                }}
              />
            ) : selectedFile && selectedFile.endsWith('.md') ? (
              <ReactMarkdown
                components={{
                  h1: ({ children }) => (
                    <p
                      style={{
                        fontSize: '1.2rem',
                        fontWeight: 600,
                        margin: '0.75rem 0 0.4rem',
                      }}>
                      {children}
                    </p>
                  ),
                  h2: ({ children }) => (
                    <p
                      style={{
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        margin: '0.6rem 0 0.3rem',
                      }}>
                      {children}
                    </p>
                  ),
                  h3: ({ children }) => (
                    <p
                      style={{
                        fontSize: '1rem',
                        fontWeight: 600,
                        margin: '0.5rem 0 0.25rem',
                      }}>
                      {children}
                    </p>
                  ),
                  h4: ({ children }) => (
                    <p
                      style={{
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        margin: '0.4rem 0 0.2rem',
                      }}>
                      {children}
                    </p>
                  ),
                  h5: ({ children }) => (
                    <p
                      style={{
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        margin: '0.4rem 0 0.2rem',
                      }}>
                      {children}
                    </p>
                  ),
                  h6: ({ children }) => (
                    <p
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        margin: '0.4rem 0 0.2rem',
                      }}>
                      {children}
                    </p>
                  ),
                }}>
                {fileContent}
              </ReactMarkdown>
            ) : (
              <pre>{fileContent}</pre>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default UserFiles;
